import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getBaseUrl } from "@/lib/site";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Calendar as CalendarIcon, Check, Copy, ExternalLink, Loader2, Plus, Eye, Sparkles, Users, Download, Image, Video, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { MapPicker } from "@/components/maps/MapPicker";
import { PlacesAutocompleteInput } from "@/components/maps/PlacesAutocompleteInput";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type EventMediaItem = {
  url: string;
  type?: "image" | "video";
};

type EventRow = {
  id: string;
  business_id: string;
  title: string;
  slug: string;
  description: string | null;
  venue_name: string | null;
  venue_address: string | null;
  venue_place_id?: string | null;
  venue_lat?: number | null;
  venue_lng?: number | null;
  starts_at: string;
  ends_at: string | null;
  cover_image_url: string | null;
  status: "draft" | "published" | "cancelled";
  capacity: number | null;
  price_amount?: number | null;
  currency?: string | null;
  created_at: string;
  updated_at: string;
  media_items?: EventMediaItem[] | null;
};

function eventUrl(origin: string, businessSlug: string, eventSlug: string) {
  return `${origin}/events/${businessSlug}/${eventSlug}`;
}

// Helper to derive media items for thumbnails (supports new media_items and legacy cover_image_url)
function getEventMediaItems(event: EventRow): { url: string; type: "image" | "video" }[] {
  const items = (event as any).media_items as EventMediaItem[] | null | undefined;
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item) => {
      const url = item.url;
      const lower = (url || "").toLowerCase();
      const isVideo =
        lower.endsWith(".mp4") ||
        lower.endsWith(".webm") ||
        lower.endsWith(".mov") ||
        lower.endsWith(".avi") ||
        lower.endsWith(".mkv");
      return {
        url,
        type: item.type ?? (isVideo ? "video" : "image"),
      };
    });
  }

  if (event.cover_image_url) {
    const lower = event.cover_image_url.toLowerCase();
    const isVideo =
      lower.endsWith(".mp4") ||
      lower.endsWith(".webm") ||
      lower.endsWith(".mov") ||
      lower.endsWith(".avi") ||
      lower.endsWith(".mkv");
    return [
      {
        url: event.cover_image_url,
        type: isVideo ? "video" : "image",
      },
    ];
  }

  return [];
}

export default function Events() {
  const { business, loading: businessLoading } = useBusiness();
  const { toast } = useToast();
  const sb = supabase as any;

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [eventSeatsUsed, setEventSeatsUsed] = useState<Record<string, number>>({});
  const [query, setQuery] = useState("");
  const [shareCopied, setShareCopied] = useState(false);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<EventRow | null>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const [formMedia, setFormMedia] = useState<{ file: File; preview: string; type: 'image' | 'video' }[]>([]);
  const [venueLat, setVenueLat] = useState<number | null>(null);
  const [venueLng, setVenueLng] = useState<number | null>(null);
  const [venuePlaceId, setVenuePlaceId] = useState<string>("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [regsOpen, setRegsOpen] = useState(false);
  const [regsLoading, setRegsLoading] = useState(false);
  const [regsEvent, setRegsEvent] = useState<EventRow | null>(null);
  const [regs, setRegs] = useState<any[]>([]);
  const [regsQuery, setRegsQuery] = useState("");
  const [confirmingRegId, setConfirmingRegId] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    venue_name: "",
    venue_address: "",
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    cover_image_url: "",
    capacity: "",
    price_amount: "",
    status: "draft" as "draft" | "published" | "cancelled",
  });

  const [ticketTypes, setTicketTypes] = useState<
    Array<{
      id?: string;
      name: string;
      description: string;
      price_amount: string;
      duration_value: string;
      duration_unit: "days" | "weeks" | "months";
      sort_order: number;
      _removed?: boolean;
    }>
  >([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const durationToDays = (value: number, unit: "days" | "weeks" | "months") => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    if (unit === "weeks") return value * 7;
    if (unit === "months") return value * 30;
    return value;
  };

  const eventDurationDays = useMemo(() => {
    if (!form.start_date || !form.start_time || !form.end_date || !form.end_time) return null;
    const startDT = new Date(`${form.start_date}T${form.start_time}`);
    const endDT = new Date(`${form.end_date}T${form.end_time}`);
    if (Number.isNaN(startDT.getTime()) || Number.isNaN(endDT.getTime())) return null;
    const diff = endDT.getTime() - startDT.getTime();
    if (diff <= 0) return null;
    return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [form.start_date, form.start_time, form.end_date, form.end_time]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => {
      return (
        e.title.toLowerCase().includes(q) ||
        String(e.venue_name || "").toLowerCase().includes(q) ||
        String(e.slug || "").toLowerCase().includes(q)
      );
    });
  }, [events, query]);

  const fetchEvents = async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const [{ data: eventsData, error: eErr }, { data: regsData, error: rErr }] = await Promise.all([
        sb
          .from("events")
          .select("*")
          .eq("business_id", business.id)
          .order("starts_at", { ascending: false }),
        // lightweight stats for capacity left
        sb
          .from("event_registrations")
          .select("event_id,quantity,status")
          .eq("business_id", business.id)
          .limit(5000),
      ]);
      if (eErr) throw eErr;
      if (rErr) throw rErr;
      setEvents((eventsData || []) as EventRow[]);

      const used: Record<string, number> = {};
      for (const r of regsData || []) {
        if (String(r.status || "") === "cancelled") continue;
        const eid = String(r.event_id);
        used[eid] = (used[eid] || 0) + Number(r.quantity || 1);
      }
      setEventSeatsUsed(used);
    } catch (e: any) {
      toast({ title: "Failed to load events", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const resetForm = () => {
    setEditing(null);
    setFormMedia([]);
    if (mediaInputRef.current) mediaInputRef.current.value = "";
    setVenueLat(null);
    setVenueLng(null);
    setVenuePlaceId("");
    setStartDate(undefined);
    setEndDate(undefined);
    setForm({
      title: "",
      description: "",
      venue_name: "",
      venue_address: "",
      start_date: "",
      start_time: "",
      end_date: "",
      end_time: "",
      cover_image_url: "",
      capacity: "",
      price_amount: "",
      status: "draft",
    });
  };

  const openCreate = () => {
    resetForm();
    const now = new Date();
    const startLocal = new Date(now.getTime() + 60 * 60 * 1000); // 1hr
    const endLocal = new Date(startLocal.getTime() + 2 * 60 * 60 * 1000);
    setStartDate(startLocal);
    setEndDate(endLocal);
    setForm((f) => ({
      ...f,
      start_date: format(startLocal, "yyyy-MM-dd"),
      start_time: format(startLocal, "HH:mm"),
      end_date: format(endLocal, "yyyy-MM-dd"),
      end_time: format(endLocal, "HH:mm"),
    }));
    setTicketTypes([]);
    setOpen(true);
  };

  const openEdit = async (e: EventRow) => {
    setEditing(e);
    
    // Load existing media items
    const { data: eventData } = await supabase
      .from("events" as any)
      .select("media_items")
      .eq("id", e.id)
      .single();
    
    const mediaItems = (eventData as any)?.media_items;
    if (Array.isArray(mediaItems) && mediaItems.length > 0) {
      // For existing media, we can't create File objects, so we'll store them as preview URLs
      setFormMedia(mediaItems.map((item: { url: string; type: 'image' | 'video' }) => ({
        file: new File([], item.url.split('/').pop() || 'existing-media'), // Dummy file for existing media
        preview: item.url,
        type: item.type,
      })));
    } else if (e.cover_image_url) {
      // Fallback to cover_image_url for backward compatibility
      const url = e.cover_image_url.toLowerCase();
      const isVideo = url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.endsWith('.mkv');
      setFormMedia([{
        file: new File([], e.cover_image_url.split('/').pop() || 'existing-media'),
        preview: e.cover_image_url,
        type: isVideo ? 'video' : 'image',
      }]);
    } else {
      setFormMedia([]);
    }
    
    if (mediaInputRef.current) mediaInputRef.current.value = "";
    setVenueLat(typeof e.venue_lat === "number" ? e.venue_lat : null);
    setVenueLng(typeof e.venue_lng === "number" ? e.venue_lng : null);
    setVenuePlaceId(String(e.venue_place_id || ""));
    const startLocal = e.starts_at ? new Date(e.starts_at) : null;
    const endLocal = e.ends_at ? new Date(e.ends_at) : null;
    setStartDate(startLocal || undefined);
    setEndDate(endLocal || undefined);
    setForm({
      title: e.title,
      description: e.description || "",
      venue_name: e.venue_name || "",
      venue_address: e.venue_address || "",
      start_date: startLocal ? format(startLocal, "yyyy-MM-dd") : "",
      start_time: startLocal ? format(startLocal, "HH:mm") : "",
      end_date: endLocal ? format(endLocal, "yyyy-MM-dd") : "",
      end_time: endLocal ? format(endLocal, "HH:mm") : "",
      cover_image_url: e.cover_image_url || "",
      capacity: e.capacity == null ? "" : String(e.capacity),
      price_amount: (e as any).price_amount == null ? "" : String((e as any).price_amount),
      status: e.status,
    });
    // Load ticket types for this event (best-effort)
    (async () => {
      try {
        const { data } = await sb
          .from("event_ticket_types")
          .select("id,name,description,price_amount,duration_value,duration_unit,sort_order,is_active")
          .eq("event_id", e.id)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });
        const active = (data || []).filter((t: any) => t.is_active !== false);
        setTicketTypes(
          active.map((t: any, idx: number) => ({
            id: t.id,
            name: String(t.name || ""),
            description: String(t.description || ""),
            price_amount: t.price_amount == null ? "" : String(t.price_amount),
            duration_value: String(t.duration_value ?? 1),
            duration_unit: (t.duration_unit || "days") as any,
            sort_order: Number(t.sort_order ?? idx),
          })),
        );
      } catch {
        setTicketTypes([]);
      }
    })();
    setOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList | null } }) => {
    const files = e.target.files;
    if (!files) return;
    
    const newMedia: { file: File; preview: string; type: 'image' | 'video' }[] = [];
    
    Array.from(files).forEach((file) => {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      
      if (!isVideo && !isImage) {
        toast({ title: "Invalid file", description: "Please upload images or videos only.", variant: "destructive" });
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: "File too large", description: "Max size is 10MB per file.", variant: "destructive" });
        return;
      }
      
      const preview = URL.createObjectURL(file);
      newMedia.push({
        file,
        preview,
        type: isVideo ? 'video' : 'image',
      });
    });
    
    setFormMedia([...formMedia, ...newMedia]);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    const newMedia = [...formMedia];
    URL.revokeObjectURL(newMedia[index].preview);
    newMedia.splice(index, 1);
    setFormMedia(newMedia);
  };

  const save = async () => {
    if (!business?.id) return;
    if (!form.title.trim() || !form.start_date || !form.start_time) {
      toast({ title: "Missing fields", description: "Title, start date and start time are required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      // Process media items: upload new ones, keep existing ones
      let coverUrl: string | null = null;
      let mediaItems: Array<{ url: string; type: 'image' | 'video' }> = [];
      
      if (formMedia.length > 0 && business) {
        const processedMedia = await Promise.all(
          formMedia.map(async (media) => {
            // Check if this is an existing media item (has a preview URL that's already a full URL)
            // New uploads will have blob: URLs as preview, existing ones will have https:// URLs
            const isExisting = media.preview.startsWith('http://') || media.preview.startsWith('https://');
            
            if (isExisting) {
              // This is existing media, use the preview URL directly
              return {
                url: media.preview,
                type: media.type,
              };
            } else {
              // This is new media, upload it
              const fileExt = media.file.name.split('.').pop();
              const fileName = `events/${business.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
              
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('service-media')
                .upload(fileName, media.file, {
                  cacheControl: '3600',
                  upsert: false
                });
              
              if (uploadError) {
                throw uploadError;
              }
              
              // Get public URL
              const { data: urlData } = supabase.storage
                .from('service-media')
                .getPublicUrl(uploadData.path);
              
              return {
                url: urlData.publicUrl,
                type: media.type,
              };
            }
          })
        );

        mediaItems = processedMedia;
        // Set cover_image_url to first media item for backward compatibility
        if (mediaItems.length > 0) {
          coverUrl = mediaItems[0].url;
        }
      }

      const startDT = new Date(`${form.start_date}T${form.start_time}`);
      const hasEnd = !!form.end_date && !!form.end_time;
      const endDT = hasEnd ? new Date(`${form.end_date}T${form.end_time}`) : null;

      const activeTicketTypes = ticketTypes.filter((t) => !t._removed);
      if (activeTicketTypes.length > 0) {
        if (!endDT) {
          toast({
            title: "End date required",
            description: "Set an end date/time to use ticket tiers.",
            variant: "destructive",
          });
          return;
        }
        const evDays = eventDurationDays;
        if (!evDays) {
          toast({
            title: "Invalid event duration",
            description: "End time must be after start time.",
            variant: "destructive",
          });
          return;
        }
        for (const t of activeTicketTypes) {
          const durVal = Number(t.duration_value || 0);
          const durDays = durationToDays(durVal, t.duration_unit);
          if (!t.name.trim()) {
            toast({ title: "Ticket tier missing name", description: "Each ticket tier needs a name.", variant: "destructive" });
            return;
          }
          if (durDays <= 0) {
            toast({ title: "Invalid tier duration", description: "Each ticket tier must have a duration.", variant: "destructive" });
            return;
          }
          if (durDays > evDays) {
            toast({
              title: "Tier duration too long",
              description: `“${t.name}” is ${durDays} days but the event duration is ${evDays} days.`,
              variant: "destructive",
            });
            return;
          }
        }
      }

      const payload: any = {
        business_id: business.id,
        title: form.title.trim(),
        description: form.description.trim() || null,
        venue_name: form.venue_name.trim() || null,
        venue_address: form.venue_address.trim() || null,
        starts_at: startDT.toISOString(),
        ends_at: endDT ? endDT.toISOString() : null,
        cover_image_url: coverUrl, // Keep for backward compatibility
        media_items: mediaItems.length > 0 ? mediaItems : null, // New field for multiple media
        capacity: form.capacity ? Number(form.capacity) : null,
        price_amount: form.price_amount ? Number(form.price_amount) : null,
        currency: (business as any)?.currency || "GHS",
        status: form.status,
        venue_place_id: venuePlaceId || null,
        venue_lat: typeof venueLat === "number" ? venueLat : null,
        venue_lng: typeof venueLng === "number" ? venueLng : null,
      };

      let eventId = editing?.id as string | undefined;
      if (editing) {
        const { data: updated, error } = await sb.from("events").update(payload).eq("id", editing.id).select("id").single();
        if (error) throw error;
        eventId = updated?.id;
        toast({ title: "Event updated" });
      } else {
        const { data: inserted, error } = await sb.from("events").insert(payload).select("id").single();
        if (error) throw error;
        eventId = inserted?.id;
        toast({ title: "Event created" });
      }

      // Save ticket types (best-effort, non-breaking). Uses archive (is_active=false) for removals.
      if (eventId) {
        const removedIds = ticketTypes.filter((t) => t._removed && t.id).map((t) => t.id) as string[];
        if (removedIds.length) {
          await sb.from("event_ticket_types").update({ is_active: false }).in("id", removedIds);
        }

        // Separate new ticket types from existing ones
        const existingTicketTypes = activeTicketTypes.filter((t) => t.id);
        const newTicketTypes = activeTicketTypes.filter((t) => !t.id);

        // Update existing ticket types
        if (existingTicketTypes.length > 0) {
          for (const t of existingTicketTypes) {
            await sb
              .from("event_ticket_types")
              .update({
                name: t.name.trim(),
                description: t.description.trim() || null,
                price_amount: t.price_amount ? Number(t.price_amount) : null,
                currency: (business as any)?.currency || "GHS",
                duration_value: Math.max(1, Number(t.duration_value || 1)),
                duration_unit: t.duration_unit,
                sort_order: Number.isFinite(t.sort_order) ? t.sort_order : 0,
                is_active: true,
              })
              .eq("id", t.id);
          }
        }

        // Insert new ticket types
        if (newTicketTypes.length > 0) {
          const inserts = newTicketTypes.map((t, idx) => ({
          business_id: business.id,
          event_id: eventId,
          name: t.name.trim(),
          description: t.description.trim() || null,
          price_amount: t.price_amount ? Number(t.price_amount) : null,
          currency: (business as any)?.currency || "GHS",
          duration_value: Math.max(1, Number(t.duration_value || 1)),
          duration_unit: t.duration_unit,
          sort_order: Number.isFinite(t.sort_order) ? t.sort_order : idx,
          is_active: true,
        }));
          await sb.from("event_ticket_types").insert(inserts);
        }
      }

      setOpen(false);
      resetForm();
      await fetchEvents();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const origin = getBaseUrl();
  const businessSlug = business?.slug || "";
  const publishedEvent = events.find((event) => event.status === "published") || null;
  const shareUrl = businessSlug
    ? eventUrl(origin, businessSlug, publishedEvent?.slug || "your-event")
    : `${origin}/events/your-business/your-event`;

  const handleCopyShareUrl = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    window.setTimeout(() => setShareCopied(false), 2000);
    toast({ title: publishedEvent ? "Event link copied" : "Event URL format copied" });
  };

  const handleOpenShareUrl = () => {
    if (!businessSlug || !publishedEvent) return;
    window.open(eventUrl(origin, businessSlug, publishedEvent.slug), "_blank");
  };

  const copyUrl = async (e: EventRow) => {
    if (!businessSlug) return;
    const url = eventUrl(origin, businessSlug, e.slug);
    await navigator.clipboard.writeText(url);
    toast({ title: "Event link copied" });
  };

  const previewInNewTab = (e: EventRow) => {
    if (!businessSlug) return;
    window.open(eventUrl(origin, businessSlug, e.slug), "_blank");
  };

  const openRegistrations = async (e: EventRow) => {
    setRegsEvent(e);
    setRegsOpen(true);
    setRegsQuery("");
    setRegsLoading(true);
    try {
      const { data, error } = await sb
        .from("event_registrations")
        .select("id,name,email,phone,quantity,status,ticket_code,created_at,unit_price_amount,unit_currency")
        .eq("event_id", e.id)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      setRegs(data || []);
    } catch (err: any) {
      toast({ title: "Failed to load registrations", description: err?.message || "Try again.", variant: "destructive" });
    } finally {
      setRegsLoading(false);
    }
  };

  const exportRegsCSV = () => {
    const rows = filteredRegs.map((r: any) => ({
      name: r.name,
      email: r.email || "",
      phone: r.phone || "",
      quantity: r.quantity || 1,
      ticket_code: r.ticket_code || "",
      status: r.status || "",
      registered_at: r.created_at ? format(new Date(r.created_at), "yyyy-MM-dd h:mm a") : "",
    }));
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map((r: any) => headers.map((h) => esc((r as any)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `event_registrations_${regsEvent?.slug || "event"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const confirmRegistrationPayment = async (registration: any) => {
    if (!regsEvent || !business?.id) return;
    setConfirmingRegId(registration.id);
    try {
      const paidAt = new Date().toISOString();
      let { error } = await sb
        .from("event_registrations")
        .update({ status: "registered", paid_at: paidAt } as any)
        .eq("id", registration.id);
      if (error && String(error.message || "").includes("paid_at")) {
        const fallback = await sb
          .from("event_registrations")
          .update({ status: "registered" } as any)
          .eq("id", registration.id);
        error = fallback.error;
      }
      if (error) throw error;

      const ticketUrl =
        businessSlug && regsEvent.slug
          ? `${getBaseUrl()}/events/${businessSlug}/${regsEvent.slug}/ticket/${registration.id}`
          : "";

      const notificationTasks: Array<Promise<any>> = [];
      if (registration.email) {
        notificationTasks.push(
          supabase.functions.invoke("send-email", {
            body: { type: "event_registration", registrationId: registration.id },
          }),
        );
      }
      if (registration.phone) {
        const message = [
          `Your ticket for ${regsEvent.title} is confirmed.`,
          registration.ticket_code ? `Ticket: ${registration.ticket_code}.` : null,
          ticketUrl ? `View: ${ticketUrl}` : null,
        ]
          .filter(Boolean)
          .join(" ");
        notificationTasks.push(
          supabase.functions.invoke("send-sms", {
            body: {
              businessId: business.id,
              phone: registration.phone,
              message,
              context: "event_registration_confirmed",
              contextId: registration.id,
            },
          }),
        );
      }

      const results = await Promise.allSettled(notificationTasks);
      const failedNotifications = results.filter((result) => {
        if (result.status === "rejected") return true;
        return !!result.value?.error;
      }).length;

      setRegs((current) =>
        current.map((item) =>
          item.id === registration.id ? { ...item, status: "registered", paid_at: paidAt } : item,
        ),
      );

      toast({
        title: "Registration confirmed",
        description:
          notificationTasks.length === 0
            ? "No email or phone was available for ticket delivery."
            : failedNotifications > 0
              ? "Ticket confirmed, but one notification could not be sent."
              : "Ticket confirmation sent.",
      });
    } catch (err: any) {
      toast({
        title: "Failed to confirm registration",
        description: err?.message || "Try again.",
        variant: "destructive",
      });
    } finally {
      setConfirmingRegId(null);
    }
  };

  const filteredRegs = useMemo(() => {
    const q = regsQuery.trim().toLowerCase();
    if (!q) return regs;
    return regs.filter((r: any) => {
      return (
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.email || "").toLowerCase().includes(q) ||
        String(r.phone || "").toLowerCase().includes(q) ||
        String(r.ticket_code || "").toLowerCase().includes(q)
      );
    });
  }, [regs, regsQuery]);

  const regsSummary = useMemo(() => {
    const totalRegistrations = regs.length;
    const totalAttendees = regs.reduce((sum: number, r: any) => sum + Number(r.quantity || 1), 0);
    const cancelled = regs.filter((r: any) => String(r.status || "") === "cancelled").length;
    const checkedIn = regs.filter((r: any) => String(r.status || "") === "checked_in").length;
    const pending = regs.filter((r: any) => String(r.status || "") === "pending").length;
    const capacity = regsEvent?.capacity ?? null;
    
    // Calculate used seats (only non-cancelled registrations)
    const used = regs.reduce((sum: number, r: any) => {
      if (String(r.status || "") === "cancelled") return sum;
      return sum + Number(r.quantity || 1);
    }, 0);
    const left = typeof capacity === "number" ? Math.max(0, capacity - used) : null;
    
    // Calculate revenue: only count paid registrations (status = 'registered')
    // Use actual unit_price_amount from each registration (supports ticket types with different prices)
    const revenue = regs.reduce((sum: number, r: any) => {
      // Only count registered (paid) registrations, not pending or cancelled
      if (String(r.status || "") !== "registered") return sum;
      const unitPrice = r.unit_price_amount != null ? Number(r.unit_price_amount) : null;
      if (unitPrice == null || unitPrice <= 0) return sum;
      const qty = Number(r.quantity || 1);
      return sum + (unitPrice * qty);
    }, 0);
    
    return { totalRegistrations, totalAttendees, cancelled, checkedIn, pending, capacity, used, left, revenue: revenue > 0 ? revenue : null };
  }, [regs, regsEvent]);

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Events"
        description="Create and share event pages"
        action={
          <Button size="pill" onClick={openCreate} disabled={businessLoading}>
            <Plus className="mr-2 h-4 w-4" />
            New Event
          </Button>
        }
      />

      <div className="mb-6 space-y-3">
        <div className="glass-card space-y-3 p-3 sm:flex sm:items-center sm:gap-4 sm:space-y-0 sm:p-4">
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs text-muted-foreground sm:text-sm">
              {publishedEvent ? "Your event page" : "Your event page format"}
            </p>
            <code className="block truncate rounded bg-accent px-2 py-1 text-xs text-foreground sm:text-sm">
              {shareUrl}
            </code>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyShareUrl} className="flex-1 sm:flex-none">
              {shareCopied ? <Check className="mr-1 h-4 w-4 sm:mr-2" /> : <Copy className="mr-1 h-4 w-4 sm:mr-2" />}
              {shareCopied ? "Copied" : "Copy"}
            </Button>
            <Button
              size="sm"
              onClick={handleOpenShareUrl}
              disabled={!businessSlug || !publishedEvent}
              className="flex-1 sm:flex-none"
            >
              <ExternalLink className="mr-1 h-4 w-4 sm:mr-2" />
              Open
            </Button>
          </div>
        </div>

        {!publishedEvent && (
          <p className="text-xs text-muted-foreground">
            Publish an event to enable the live share link.
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-2">
          <Label>Search events</Label>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by title, venue, slug…" />
        </div>
        <div className="text-xs text-muted-foreground">
          Search by title, venue, or slug.
        </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Create your first event</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add an event, publish it, and share the link with attendees.
              </p>
              <Button className="mt-4" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                New Event
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((e) => {
            const start = e.starts_at ? format(new Date(e.starts_at), "MMM d, yyyy • h:mm a") : "";
            const isPublished = e.status === "published";
            const url = businessSlug ? eventUrl(origin, businessSlug, e.slug) : "";
            const used = eventSeatsUsed[e.id] || 0;
            const hasCapacity = typeof e.capacity === "number" && e.capacity != null;
            const left = hasCapacity ? Math.max(0, Number(e.capacity) - used) : null;
            const currency = (e.currency || (business as any)?.currency || "GHS") as string;
            const price = (e as any).price_amount == null ? null : Number((e as any).price_amount);
            const mediaItems = getEventMediaItems(e);
            const thumb = mediaItems[0];
            return (
              <div key={e.id} className="rounded-xl border border-border bg-card overflow-hidden shadow-soft">
                <div className="relative">
                  {thumb ? (
                    thumb.type === "video" ? (
                      <video
                        src={thumb.url}
                        className="h-40 w-full object-cover"
                        muted
                        playsInline
                        autoPlay
                        loop
                      />
                    ) : (
                      <img src={thumb.url} alt={e.title} className="h-40 w-full object-cover" />
                    )
                  ) : (
                    <div className="h-40 w-full bg-gradient-to-br from-primary/15 via-primary/5 to-background flex items-center justify-center">
                      <CalendarIcon className="h-10 w-10 text-primary" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <Badge variant={isPublished ? "default" : "secondary"} className="capitalize">
                      {e.status}
                    </Badge>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-semibold text-foreground line-clamp-2">{e.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{start}</p>
                  {e.venue_name ? <p className="text-xs text-muted-foreground mt-1">{e.venue_name}</p> : null}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{price == null ? "Free" : `${currency} ${price.toFixed(2)}`}</Badge>
                    {hasCapacity ? (
                      <>
                        <Badge variant={left === 0 ? "destructive" : "outline"}>Seats left: {left}</Badge>
                        <span className="text-xs text-muted-foreground">({used}/{e.capacity})</span>
                      </>
                    ) : null}
                  </div>
                  {isPublished ? (
                    <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border bg-accent/20 px-3 py-2">
                      <p className="text-xs text-muted-foreground font-mono truncate">{url}</p>
                      <Button size="sm" variant="ghost" onClick={() => copyUrl(e)} disabled={!businessSlug}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-3">
                      Publish the event to enable the public link.
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(e)} className="flex-1 sm:flex-none">
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openRegistrations(e)} className="flex-1 sm:flex-none">
                      <Users className="mr-2 h-4 w-4" />
                      Registrations
                    </Button>
                    <Button size="sm" onClick={() => previewInNewTab(e)} disabled={!businessSlug || !isPublished}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => window.open(`${getBaseUrl()}/app/events/preview/${e.id}`, "_blank")}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Preview
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        modal={false}
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) resetForm();
        }}
      >
        <DialogContent
          className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col"
          onInteractOutside={(e) => {
            // Google Places dropdown is rendered outside the dialog (body),
            // so clicking it looks like an outside click. Prevent the dialog from closing.
            const t = e.target as HTMLElement | null;
            if (t?.closest?.(".pac-container")) e.preventDefault();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{editing ? "Edit event" : "New event"}</DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0 space-y-4">
            <div className="space-y-2">
              <Label>Photos & Videos</Label>
              <p className="text-xs text-muted-foreground">
                Add photos or short videos to showcase this event. They will auto-play in a carousel on the event page.
              </p>
              
              {/* Media Preview */}
              {formMedia.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formMedia.map((media, index) => (
                    <div key={index} className="relative group">
                      {media.type === 'image' ? (
                        <img 
                          src={media.preview} 
                          alt={`Event media ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border border-border"
                        />
                      ) : (
                        <div className="relative w-20 h-20 rounded-lg border border-border overflow-hidden bg-muted">
                          <video 
                            src={media.preview}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Video className="h-6 w-6 text-white" />
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeMedia(index)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <input
                ref={mediaInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <div
                className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => mediaInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const files = e.dataTransfer.files;
                  if (files.length > 0) handleFileSelect({ target: { files } } as any);
                }}
              >
                <div className="text-sm text-muted-foreground">
                  Drag & drop images or videos here, or click to upload
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Recommended: wide images (e.g. 1200×600), max 10MB per file.</p>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                  </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What is this event about?"
                className="min-h-[90px]"
              />
                  </div>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Start date & time</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between" disabled={saving}>
                        <span className={startDate ? "text-foreground" : "text-muted-foreground"}>
                          {startDate ? format(startDate, "MMM d, yyyy") : "Select start date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(d) => {
                          if (!d) {
                            setStartDate(undefined);
                            setForm((f) => ({ ...f, start_date: "" }));
                            return;
                          }
                          setStartDate(d);
                          setForm((f) => ({ ...f, start_date: format(d, "yyyy-MM-dd") }));
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    disabled={saving}
                  />
                  </div>
              </div>
              <div className="space-y-2">
                <Label>End date & time</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between" disabled={saving}>
                        <span className={endDate ? "text-foreground" : "text-muted-foreground"}>
                          {endDate ? format(endDate, "MMM d, yyyy") : "Select end date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(d) => {
                          if (!d) {
                            setEndDate(undefined);
                            setForm((f) => ({ ...f, end_date: "" }));
                            return;
                          }
                          setEndDate(d);
                          setForm((f) => ({ ...f, end_date: format(d, "yyyy-MM-dd") }));
                        }}
                        initialFocus
                        disabled={(date) => startDate ? date < startDate : false}
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Required if you want to add ticket tiers (e.g. 2 weeks, 4 weeks, 3 months).
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Venue name</Label>
                <Input value={form.venue_name} onChange={(e) => setForm((f) => ({ ...f, venue_name: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Capacity (optional)</Label>
                <Input type="number" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Price (optional)</Label>
                  <Input
                    type="number"
                    value={(form as any).price_amount || ""}
                    onChange={(e) => setForm((f: any) => ({ ...f, price_amount: e.target.value }))}
                placeholder={`e.g. ${(business as any)?.currency || "GHS"} 20`}
                    disabled={saving || ticketTypes.filter((t) => !t._removed).length > 0}
                  />
              <p className="text-xs text-muted-foreground">
                Leave empty for a free event. If you add ticket tiers, this base price is not used.
              </p>
            </div>

            {/* Ticket Types (tiers) */}
            <div className="space-y-3 rounded-xl border border-border bg-accent/20 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="font-semibold text-foreground">Ticket types (tiers)</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional. Add tiers like "2 weeks", "4 weeks", "3 months". Each tier duration must fit within the event duration.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setTicketTypes((prev) => [
                      ...prev,
                      {
                        name: "",
                        description: "",
                        price_amount: "",
                        duration_value: "1",
                        duration_unit: "weeks",
                        sort_order: prev.length,
                      },
                    ]);
                  }}
                  disabled={saving}
                  className="w-full sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add tier
                </Button>
              </div>

              {ticketTypes.filter((t) => !t._removed).length === 0 ? (
                <p className="text-xs text-muted-foreground">No tiers added yet. The event will use the base price above.</p>
              ) : null}

              <div className="space-y-3">
                  {ticketTypes
                    .map((t, idx) => ({ t, idx }))
                    .filter(({ t }) => !t._removed)
                    .map(({ t, idx }) => {
                      const durVal = Math.max(0, Number(t.duration_value || 0));
                      const durDays = durationToDays(durVal, t.duration_unit);
                      const durOk = eventDurationDays == null ? false : durDays > 0 && durDays <= eventDurationDays;
                      return (
                      <div key={t.id || `new-${idx}`} className="rounded-lg border border-border bg-background p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 space-y-2">
                            <Label className="text-sm">Tier name</Label>
                              <Input
                                value={t.name}
                                onChange={(e) =>
                                  setTicketTypes((prev) =>
                                    prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)),
                                  )
                                }
                              placeholder="e.g. 4 weeks lessons"
                                disabled={saving}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                            className="text-destructive shrink-0 mt-7"
                              onClick={() => {
                                setTicketTypes((prev) => prev.map((x, i) => (i === idx ? { ...x, _removed: true } : x)));
                              }}
                              disabled={saving}
                            >
                              Remove
                            </Button>
                          </div>
                          
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm">Price</Label>
                            <Input
                              type="number"
                              value={t.price_amount}
                              onChange={(e) =>
                                setTicketTypes((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, price_amount: e.target.value } : x)),
                                )
                              }
                              placeholder={`e.g. ${(business as any)?.currency || "GHS"} 200`}
                              disabled={saving}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Duration</Label>
                            <Input
                              type="number"
                              min={1}
                              value={t.duration_value || ""}
                              onChange={(e) => {
                                const newValue = e.target.value === "" ? "" : String(Math.max(1, parseInt(e.target.value) || 1));
                                setTicketTypes((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, duration_value: newValue } : x)),
                                );
                              }}
                              disabled={saving}
                              placeholder="1"
                            />
                            <Select
                              value={t.duration_unit}
                              onValueChange={(v: any) =>
                                setTicketTypes((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, duration_unit: v } : x)),
                                )
                              }
                              disabled={saving}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="days">Days</SelectItem>
                                <SelectItem value="weeks">Weeks</SelectItem>
                                <SelectItem value="months">Months</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className={`text-xs ${durOk ? "text-muted-foreground" : "text-destructive"}`}>
                              {eventDurationDays == null
                                ? "Set event end date/time first"
                                : durOk
                                  ? `OK: ${durDays} day(s) within ${eventDurationDays} day event`
                                  : `Error: ${durDays} day(s) exceeds ${eventDurationDays} day event`}
                            </p>
                          </div>
                        </div>
                          
                        <div className="space-y-2">
                          <Label className="text-sm">Details (optional)</Label>
                          <Textarea
                            value={t.description}
                            onChange={(e) =>
                              setTicketTypes((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)),
                              )
                            }
                            placeholder="What's included? (e.g. 2 sessions/week, materials included)"
                            className="min-h-[80px] resize-none"
                            disabled={saving}
                          />
                        </div>
                        </div>
                      );
                    })}
                </div>
            </div>
            <div className="space-y-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <Label>Venue address</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setLocationOpen(true)}
                  className="w-full sm:w-auto"
                >
                  Pick on map
                </Button>
              </div>
              <PlacesAutocompleteInput
                value={form.venue_address}
                onValueChange={(v) => {
                  setForm((f) => ({ ...f, venue_address: v }));
                  if (!v) {
                    setVenuePlaceId("");
                    setVenueLat(null);
                    setVenueLng(null);
                  }
                }}
                onPlaceSelected={(p: any) => {
                  setForm((f) => ({ ...f, venue_address: p.formattedAddress }));
                  setVenuePlaceId(p.placeId || "");
                  setVenueLat(typeof p.lat === "number" ? p.lat : null);
                  setVenueLng(typeof p.lng === "number" ? p.lng : null);
                }}
                placeholder="Search a place or type an address"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex flex-wrap gap-2">
                {(["draft", "published", "cancelled"] as const).map((s) => (
                  <Button
                    key={s}
                    type="button"
                    variant={form.status === s ? "default" : "outline"}
                    size="sm"
                    onClick={() => setForm((f) => ({ ...f, status: s }))}
                  >
                    {s}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Only <span className="font-medium text-foreground">published</span> events have a public URL.
              </p>
            </div>

          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} className="flex-1 rounded-lg">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-lg">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog modal={false} open={locationOpen} onOpenChange={setLocationOpen}>
        <DialogContent className="sm:max-w-2xl w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Pick venue location</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <MapPicker
              address={form.venue_address}
              onAddressChange={(v) => {
                setForm((f) => ({ ...f, venue_address: v }));
                if (!v) {
                  setVenuePlaceId("");
                  setVenueLat(null);
                  setVenueLng(null);
                }
              }}
              lat={venueLat}
              lng={venueLng}
              onLatLngChange={(pos) => {
                setVenueLat(pos?.lat ?? null);
                setVenueLng(pos?.lng ?? null);
              }}
              onPlaceSelected={(p: any) => {
                setForm((f) => ({ ...f, venue_address: p.formattedAddress }));
                setVenuePlaceId(p.placeId || "");
                setVenueLat(typeof p.lat === "number" ? p.lat : null);
                setVenueLng(typeof p.lng === "number" ? p.lng : null);
              }}
              disabled={saving}
            />
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setLocationOpen(false)} className="flex-1 rounded-lg">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={regsOpen}
        onOpenChange={(v) => {
          setRegsOpen(v);
          if (!v) {
            setRegsEvent(null);
            setRegs([]);
            setRegsQuery("");
          }
        }}
      >
        <DialogContent className="w-[calc(100%-1rem)] max-w-5xl max-h-[92vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col sm:w-[calc(100%-2rem)]">
          <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3 flex-shrink-0">
            <div>
              <DialogHeader>
                <DialogTitle>Registrations</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground mt-1">{regsEvent?.title || ""}</p>
            </div>
            <Button variant="outline" onClick={exportRegsCSV} disabled={regsLoading || filteredRegs.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>

          <div className="overflow-y-auto px-4 pb-4 flex-1 min-h-0 sm:px-6">
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">Registrations</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{regsLoading ? "…" : regsSummary.totalRegistrations}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">Attendees</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{regsLoading ? "…" : regsSummary.totalAttendees}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">Checked-in</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{regsLoading ? "…" : regsSummary.checkedIn}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{regsLoading ? "…" : regsSummary.pending}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">Revenue</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {regsLoading
                    ? "…"
                    : regsSummary.revenue == null
                      ? "—"
                      : `${(regsEvent as any)?.currency || (business as any)?.currency || "GHS"} ${Number(regsSummary.revenue).toFixed(2)}`}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">Seats left</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">
                  {regsLoading ? "…" : regsSummary.left == null ? "∞" : regsSummary.left}
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <div className="flex-1 max-w-sm">
                <Input value={regsQuery} onChange={(e) => setRegsQuery(e.target.value)} placeholder="Search name, email, phone, ticket…" />
              </div>
              <div className="text-xs text-muted-foreground">{filteredRegs.length} shown</div>
            </div>

            {regsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
              </div>
            ) : filteredRegs.length === 0 ? (
              <div className="py-10 text-sm text-muted-foreground">No registrations yet.</div>
            ) : (
              <>
              <div className="mt-4 space-y-3 md:hidden">
                {filteredRegs.map((r: any) => {
                  const status = String(r.status || "registered");
                  const isPending = status === "pending";
                  const isConfirming = confirmingRegId === r.id;

                  return (
                    <div key={r.id} className="rounded-2xl border border-border bg-background p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground">{r.name}</p>
                          <p className="mt-1 truncate text-xs text-muted-foreground">{r.email || "No email"}</p>
                          <p className="text-xs text-muted-foreground">{r.phone || "No phone"}</p>
                        </div>
                        <Badge variant={isPending ? "outline" : "secondary"} className="shrink-0 capitalize">
                          {status.replace("_", " ")}
                        </Badge>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-muted/30 p-3 text-xs">
                        <div>
                          <p className="text-muted-foreground">Qty</p>
                          <p className="mt-1 font-semibold text-foreground">{r.quantity || 1}</p>
                        </div>
                        <div className="col-span-2 min-w-0">
                          <p className="text-muted-foreground">Ticket</p>
                          <p className="mt-1 truncate font-mono font-semibold text-foreground">{r.ticket_code || "—"}</p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">
                          {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy h:mm a") : "—"}
                        </p>
                        {isPending ? (
                          <Button
                            size="sm"
                            onClick={() => confirmRegistrationPayment(r)}
                            disabled={!!confirmingRegId}
                          >
                            {isConfirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                            Confirm
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 hidden overflow-hidden rounded-xl border border-border bg-background md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRegs.map((r: any) => {
                      const status = String(r.status || "registered");
                      const isPending = status === "pending";
                      const isConfirming = confirmingRegId === r.id;

                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            <div>{r.email || "—"}</div>
                            <div>{r.phone || "—"}</div>
                          </TableCell>
                          <TableCell>{r.quantity || 1}</TableCell>
                          <TableCell className="font-mono text-xs">{r.ticket_code || "—"}</TableCell>
                          <TableCell>
                            <Badge variant={isPending ? "outline" : "secondary"} className="capitalize">
                              {status.replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy h:mm a") : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {isPending ? (
                              <Button
                                size="sm"
                                onClick={() => confirmRegistrationPayment(r)}
                                disabled={!!confirmingRegId}
                              >
                                {isConfirming ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Check className="mr-2 h-4 w-4" />
                                )}
                                Confirm payment
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


